'use client';

import { useLayoutEffect, useRef } from 'react';
import { useActivitySearchHeroBarInViewStore } from './activity-search-hero-bar-in-view-store';

/**
 * Invisible scroll sentinel marking the bottom edge of the hero filter bar. Drives
 * `heroBarInView` so the sticky filter bar and the floating mode tabs only reveal
 * once that bar has gone under the sticky site header.
 *
 * It has to sit directly after the bar rather than up in the hero: the hero's height
 * varies with the title, and a sentinel higher up passes under the header while the bar
 * itself is still on screen — which showed the duplicate controls before any scrolling
 * on short heroes. Kept separate from the bar element itself because the bar is hidden
 * at some breakpoints and a `display:none` element never intersects.
 */
export const HeroFilterBarScrollSentinel: React.FC = () => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const setInView = useActivitySearchHeroBarInViewStore.getState().setHeroBarInView;
        setInView(true);

        const io = new IntersectionObserver(
            ([entry]) => {
                if (!entry) return;
                setInView(entry.isIntersecting);
            },
            {
                root: null,
                threshold: 0,
                /** Pull the effective viewport top under the sticky site header so the bar “disappears” when covered. */
                rootMargin: '-80px 0px 0px 0px',
            },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return <div ref={ref} aria-hidden className="h-px w-full" />;
};
